import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import { Eye, EyeOff, RefreshCw } from "lucide-react";
import { siteConfig } from "../lib/siteConfig";

import { GoogleSignin, PasskeySignin } from "../components/PlayerSignin";

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [needsVerification, setNeedsVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [resendMessage, setResendMessage] = useState("");
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setResendMessage(""); setNeedsVerification(false); setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.token, data.user);
      nav("/dashboard");
    } catch (e) {
      const message = formatApiError(e);
      setErr(message);
      setNeedsVerification(e?.response?.status === 403 && /verify.*email/i.test(message));
    } finally { setLoading(false); }
  };

  const verifyCode = async () => {
    const cleanCode = verificationCode.replace(/\D/g, "");
    if (cleanCode.length !== 6) {
      setErr("Enter the 6-digit Gmail code.");
      return;
    }
    setErr("");
    setVerifyLoading(true);
    try {
      const { data } = await api.post("/auth/email/verify", { email, code: cleanCode });
      login(data.token, data.user);
      nav("/dashboard", { replace: true });
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setVerifyLoading(false);
    }
  };

  const resendVerification = async () => {
    if (!email) return;
    setErr("");
    setResendMessage("");
    setResendLoading(true);
    try {
      const { data } = await api.post("/auth/email/resend", { email });
      setResendMessage(data?.message || "Verification email sent. Check your Gmail.");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-950">
      <Navbar />
      <div className="max-w-md mx-auto px-6 pt-16 pb-24">
        <h1 className="font-display text-4xl font-black tracking-tighter mb-2">Welcome back</h1>
        <p className="text-slate-600 mb-10">Sign in to your {siteConfig.name} account.</p>
        <GoogleSignin password={password} onError={setErr} onSuccess={(data) => { login(data.token, data.user); nav("/dashboard"); }} />
        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Email</label>
            <input data-testid="login-email" type="email" required value={email} onChange={(e)=>setEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-white border border-slate-200 text-slate-950 focus:border-[#2F6FA5] focus:outline-none focus:ring-2 focus:ring-[#2F6FA5]/20" />
          </div>
          <div>
            <div className="mb-2 flex items-center justify-between gap-3">
              <label className="block text-xs uppercase tracking-[0.2em] text-slate-500">Password</label>
              <Link to="/reset-password" className="text-xs font-bold text-[#2F6FA5] hover:underline">Forgot password?</Link>
            </div>
            <div className="relative">
              <input
                data-testid="login-password"
                type={showPassword ? "text" : "password"}
                required
                value={password}
                onChange={(e)=>setPassword(e.target.value)}
                className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 pr-12 text-slate-950 [color-scheme:light] focus:border-[#2F6FA5] focus:outline-none focus:ring-2 focus:ring-[#2F6FA5]/20"
              />
              <button
                type="button"
                onClick={() => setShowPassword((value) => !value)}
                data-testid="login-toggle-password"
                className="absolute inset-y-0 right-0 grid w-12 place-items-center rounded-r-lg text-slate-500 hover:text-[#2F6FA5]"
                aria-label={showPassword ? "Hide password" : "Show password"}
                title={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          {err && <div data-testid="login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{err}</div>}
          {resendMessage && (
            <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              {resendMessage}
            </div>
          )}
          {needsVerification && (
            <div className="space-y-3">
              <input
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="Gmail code"
                className="w-full px-4 py-3 rounded-lg bg-white border border-[#2F6FA5]/30 text-slate-950 focus:border-[#2F6FA5] focus:outline-none focus:ring-2 focus:ring-[#2F6FA5]/20 text-center text-xl font-black tracking-[0.4em]"
              />
              <button
                type="button"
                onClick={verifyCode}
                disabled={verifyLoading || !email}
                className="w-full py-3 bg-[#2F6FA5] text-white font-bold rounded-full hover:bg-[#4E91C8] transition-all disabled:opacity-50"
              >
                {verifyLoading ? "Verifying..." : "Verify code & sign in"}
              </button>
              <button
                type="button"
                onClick={resendVerification}
                disabled={resendLoading || !email}
                className="w-full py-3 border border-[#2F6FA5]/40 text-[#6EA8D8] font-bold rounded-full hover:bg-[#2F6FA5]/10 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${resendLoading ? "animate-spin" : ""}`} />
                {resendLoading ? "Sending..." : "Resend Gmail code"}
              </button>
            </div>
          )}
          <button data-testid="login-submit" type="submit" disabled={loading}
            className="w-full py-3 bg-[#2F6FA5] text-white font-bold rounded-full hover:-translate-y-0.5 hover:bg-[#4E91C8] transition-all disabled:opacity-50">
            {loading ? "Signing in..." : "Sign in"}
          </button>
        </form>
        <PasskeySignin onError={setErr} onSuccess={(data) => { login(data.token, data.user); nav("/dashboard"); }} />
        <p className="mt-8 text-sm text-slate-600 text-center">
          New here? <Link to="/signup" data-testid="login-signup-link" className="text-[#2F6FA5] hover:underline">Create an account</Link>
        </p>
      </div>
    </div>
  );
}
